import React from "react";
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { Film, Tv, Star, Clock, Tag, Trash2 } from "lucide-react";
import { BlogPost } from "../../types/blog";
import { OptimizedImage } from '../common/OptimizedImage';
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { Language } from "../../types";
import { normalizeTitle } from '../../utils/stringUtils';

const Card = styled.article`
  background: var(--color-background-secondary);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    max-width: 100%;
    margin: 0;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  border-radius: 8px;
  cursor: pointer;
  
  @media (max-width: 768px) {
    height: 180px;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const Content = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 0.5rem;
  }
`;

const Title = styled.h3`
  font-size: 1rem;
  margin: 0;
  color: var(--color-text-primary);
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
  
  a {
    color: inherit;
    text-decoration: none;
    
    &:hover {
      color: var(--color-primary);
    }
  }
`;

const MetaData = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  color: var(--color-text-secondary);
  font-size: 0.75rem;
  margin-top: auto;
  
  @media (max-width: 768px) {
    gap: 0.35rem;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  
  @media (max-width: 768px) {
    padding: 0.2rem 0.4rem;
    font-size: 0.65rem;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 0.75rem;
  
  @media (max-width: 768px) {
    gap: 0.25rem;
    margin-bottom: 0.5rem;
  }
`;

const TagItem = styled.span`
  background: var(--color-primary);
  color: white;
  padding: 0.2rem 0.5rem;
  border-radius: 3px;
  font-size: 0.7rem;
  font-weight: 500;
  
  @media (max-width: 768px) {
    padding: 0.15rem 0.4rem;
    font-size: 0.65rem;
  }
`;

const Excerpt = styled.p`
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    line-height: 1.5;
    -webkit-line-clamp: 2;
  }
`;

const ReadMoreButton = styled.button`
  background: transparent;
  border: 2px solid #e2b616;
  color: #e2b616;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  align-self: flex-start;
  margin-top: 0.75rem;
  
  @media (max-width: 768px) {
    padding: 6px 14px;
    font-size: 0.85rem;
    margin-top: 0.5rem;
  }

  &:hover {
    background: #e2b616;
    color: black;
    transform: translateY(-2px);
  }
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: rgba(255, 0, 0, 0.8);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 2;
  
  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
    top: 0.35rem;
    right: 0.35rem;
  }

  &:hover {
    background: rgba(255, 0, 0, 1);
    transform: scale(1.1);
  }

  svg {
    color: white;
    width: 18px;
    height: 18px;
    
    @media (max-width: 768px) {
      width: 16px;
      height: 16px;
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.75rem;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
    margin-top: 0.5rem;
  }
`;

const DefaultImage = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-background-secondary);
  color: var(--color-text-secondary);
  
  svg {
    @media (max-width: 768px) {
      width: 24px;
      height: 24px;
    }
  }
`;

const LanguageButtons = styled.div`
  position: absolute;
  top: var(--spacing-2);
  right: var(--spacing-2);
  display: flex;
  gap: 0.5rem;
  z-index: 10;
  
  @media (max-width: 768px) {
    top: var(--spacing-1);
    right: var(--spacing-1);
    gap: 0.35rem;
  }
`;

const LanguageButton = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: rgba(0, 0, 0, 0.7);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  border: 1px solid ${props => props.$isActive ? 'var(--color-primary)' : 'transparent'};
  cursor: pointer;
  color: ${props => props.$isActive ? 'var(--color-primary)' : 'white'};
  
  @media (max-width: 768px) {
    padding: 0.2rem 0.4rem;
    font-size: 0.7rem;
  }
`;

interface BlogCardProps {
  post: BlogPost;
  onDelete?: (id: string) => void;
}

const BlogCard: React.FC<BlogCardProps> = ({ post, onDelete }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLanguage = useSelector((state: RootState) => state.language.current);

  const handleReadMore = () => {
    navigate(`/blog/${post.id}`);
  };

  const truncateText = (text: string, maxLength: number) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + "...";
  };

  const formatRating = (rating: number) => {
    return rating ? `${rating.toFixed(1)}/10` : t('blog.not_rated');
  };

  const showDeleteButton = onDelete && post.id !== "new";

  const handleLanguageChange = (lang: Language, e: React.MouseEvent) => {
    e.stopPropagation();
    i18n.changeLanguage(lang);
  };

  const getDisplayContent = () => {
    const lang = i18n.language as Language;
    if (lang === Language.FR || !post.translations?.en) {
      return {
        title: post.title,
        content: post.content,
        overview: post.overview
      };
    }

    return {
      title: post.translations.en.title,
      content: post.translations.en.content,
      overview: post.translations.en.overview
    };
  };

  const displayContent = getDisplayContent();

  return (
    <Card>
      <ImageContainer onClick={handleReadMore}>
        {showDeleteButton && (
          <DeleteButton
            onClick={(e) => {
              e.stopPropagation();
              onDelete(post.id);
            }}
          >
            <Trash2 />
          </DeleteButton>
        )}
        {post.image ? (
          <OptimizedImage
            src={post.image}
            alt={displayContent.title}
            width={800}
            height={400}
          />
        ) : (
          <DefaultImage>
            <Film size={32} />
          </DefaultImage>
        )}
        <LanguageButtons>
          <LanguageButton
            $isActive={i18n.language === Language.FR}
            onClick={(e) => handleLanguageChange(Language.FR, e)}
          >
            FR
          </LanguageButton>
          <LanguageButton
            $isActive={i18n.language === Language.EN}
            onClick={(e) => handleLanguageChange(Language.EN, e)}
          >
            EN
          </LanguageButton>
        </LanguageButtons>
      </ImageContainer>
      
      <Content>
        <Title>
          <Link to={`/blog/${post.id}`}>
            {normalizeTitle(displayContent.title)}
          </Link>
        </Title>

        <MetaData>
          <MetaItem>
            {post.type === "film" ? <Film size={16} /> : <Tv size={16} />}
            {post.type === "film" ? t('blog.film') : t('blog.serie')}
          </MetaItem>
          
          <MetaItem>
            <Star size={16} />
            {post.rating ? formatRating(post.rating) : '-'}
          </MetaItem>
          
          {post.year && (
            <MetaItem>
              {post.year}
            </MetaItem>
          )}
        </MetaData>

        {post.tags && post.tags.length > 0 && (
          <TagsContainer>
            {post.tags.map((tag, index) => (
              <TagItem key={index}>
                <Tag size={12} />
                {tag}
              </TagItem>
            ))}
          </TagsContainer>
        )}

        <Excerpt>
          {truncateText(displayContent.overview || "", 150)}
        </Excerpt>

        <ButtonContainer>
          <ReadMoreButton onClick={handleReadMore}>
            {t('blog.read_more')}
          </ReadMoreButton>
        </ButtonContainer>
      </Content>
    </Card>
  );
};

export default BlogCard;
